import React from "react";
import { motion } from "framer-motion";
import { hero, projects } from "../data/content";
import BentoCard from "../components/BentoCard";
import {
  ArrowSticker,
  CircleScribble,
  Starburst,
  Underline,
  TinyStar,
} from "../components/Stickers";

export default function Home() {
  // Split hero title into lines for staggered animation
  const lines = hero.title.split("\n");

  return (
    <main data-testid="home-page" className="relative">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 pt-12 md:pt-24 pb-20 md:pb-32"
      >
        {/* floating stickers */}
        <CircleScribble
          className="hidden md:block absolute -top-4 left-4 w-32 h-32 animate-spin-slow opacity-90"
          color="#FF1493"
        />
        <Starburst
          className="absolute top-6 left-1/3 w-8 h-8 md:w-12 md:h-12 hidden sm:block"
          color="#FF1493"
        />

        {/* Top label */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-8 md:mb-12"
        >
          <span className="tag-pill" data-testid="hero-label">
            Portfolio · 2026
          </span>
          <span className="text-sm text-[color:var(--charcoal)] hidden sm:inline">
            הכיף מתחיל כאן ↓
          </span>
        </motion.div>

        {/* Headline */}
        <h1
          data-testid="hero-title"
          className="font-display font-black tracking-tight leading-[0.95] md:leading-[0.88] text-[11.5vw] md:text-[10vw] lg:text-[8.4vw] max-w-[15ch]"
        >
          {lines.map((line, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.1 + i * 0.12,
                ease: [0.2, 0.8, 0.2, 1],
              }}
              className="block relative"
            >
              {line}
              {/* underline last line */}
              {i === lines.length - 1 && (
                <Underline
                  className="absolute right-0 -bottom-2 w-[60%] h-3"
                  color="#FF1493"
                />
              )}
            </motion.span>
          ))}
        </h1>

        {/* Side arrow sticker removed per user request */}

        {/* Intro + CTA */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-testid="hero-intro"
            className="md:col-span-7 max-w-2xl space-y-4"
          >
            {hero.intro.split("\n").map((para, i) => (
              <p
                key={i}
                className={
                  i === 0
                    ? "font-display font-bold text-xl md:text-2xl leading-snug text-[color:var(--ink)]"
                    : "text-lg md:text-xl leading-relaxed text-[color:var(--ink)]"
                }
              >
                {para}
              </p>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="md:col-span-5 flex md:justify-end"
          >
            <a
              href="#projects"
              data-testid="hero-cta"
              className="inline-flex items-center gap-3 bg-[color:var(--ink)] text-[color:var(--paper)] px-7 py-4 rounded-full font-display font-bold text-lg md:text-xl hover:bg-[color:var(--pink)] transition-colors group"
            >
              {hero.cta}
              <span className="inline-block transition-transform group-hover:-translate-x-1">
                ←
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* PROJECTS BENTO */}
      <section
        id="projects"
        data-testid="projects-section"
        className="relative mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 py-16 md:py-24"
      >
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-16 flex-wrap">
          <div className="flex items-center gap-4">
            <h2
              data-testid="projects-heading"
              className="font-display font-black text-5xl md:text-7xl leading-[0.9] tracking-tight"
            >
              עבודות
              <span className="text-[color:var(--pink)]">.</span>
            </h2>
            <span className="sticky-note mt-3 text-sm md:text-base">
              10 הנבחרות
            </span>
          </div>
        </div>

        {/* Bento Grid - 4×6 tetris layout on md+ */}
        <motion.div
          data-testid="bento-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.09,
                delayChildren: 0.05,
              },
            },
          }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 md:grid-rows-6 auto-rows-[minmax(200px,_1fr)] md:auto-rows-[minmax(220px,_1fr)] gap-4 md:gap-5"
        >
          {projects.map((p, i) => (
            <BentoCard key={p.slug} project={p} index={i} />
          ))}
        </motion.div>
      </section>
    </main>
  );
}
